/**
 * DFM checks + recommended setup for the selected orientation.
 * Each non-passing check deep-links to the design guide section that
 * explains it (mapping comes from the guide manifest's checkIds).
 */

import React from 'react';
import { Link } from 'react-router-dom';

const CHECK_LABELS = {
  envelope: 'Fits the build cell',
  overhangs: 'Overhangs',
  bridging: 'Bridging',
  bedContact: 'Bed contact & stability',
  continuousExtrusion: 'Continuous extrusion (no travel moves)',
  layerBondLoading: 'Load direction vs layer bonds',
  wallThickness: 'Wall thickness',
  warpRisk: 'Warp & shrinkage risk',
  smallFeatures: 'Small holes & features',
  trappedCavity: 'Trapped internal cavity',
  wallSlenderness: 'Single-bead wall stability'
};

const STATUS_ICON = { pass: '✓', warn: '!', fail: '✕' };

function CheckRow({ id, check, guideMap }) {
  const guideSection = guideMap && guideMap[id];
  return (
    <div className="dc-check-row">
      <span className={`dc-pill ${check.status}`}>{STATUS_ICON[check.status]} {check.status}</span>
      <div className="dc-check-body">
        <div className="dc-check-title"><b>{CHECK_LABELS[id] || id}</b></div>
        <div className="dc-check-detail">{check.detail}</div>
        {check.status !== 'pass' && guideSection && (
          <Link className="dc-check-link" to={`/design-check/guide/${guideSection.id}`}>
            Read why in the guide: {guideSection.title.replace(/^\d+:\s*/, '')} →
          </Link>
        )}
      </div>
    </div>
  );
}

function formatPrintTime(hours) {
  if (hours < 1) return `${Math.round(hours * 60)}min`;
  return `${hours}h`;
}

function PrintEstimate({ report, orientation }) {
  const estimate = orientation.estimate;
  const printMode = report.printMode;
  if (!estimate || !printMode) return null;
  const selected = estimate.modes[printMode.selected];
  if (!selected) return null;
  const alternatives = Object.values(estimate.modes).filter((m) => m.mode !== printMode.selected);

  return (
    <div className="dc-recommend-card">
      <h3>Print estimate — {selected.label}</h3>
      <div className="dc-recommend-grid">
        <div className="dc-stat"><b>~{formatPrintTime(selected.printTimeH)}</b><span>Print time</span></div>
        <div className="dc-stat"><b>{selected.weightKg}kg</b><span>Part weight ({report.material.label})</span></div>
        <div className="dc-stat"><b>{selected.layerCount}</b><span>Layers · avg {selected.layerTimeS}s each</span></div>
        <div className="dc-stat"><b>{selected.effectiveSpeedMmS}mm/s</b><span>Print speed</span></div>
      </div>
      {printMode.warning && (
        <div className="dc-recommend-note">
          <span className="dc-pill fail">✕ vase not possible</span>{' '}
          {printMode.warning}
        </div>
      )}
      {printMode.surfaceModel && (
        <div className="dc-recommend-note">
          Weight and time are for a single {report.recommended.beadWidthMm}mm bead laid along the
          surface — the mid-surface workflow used by Ai Build.
        </div>
      )}
      {selected.thermallyLimited && (
        <div className="dc-recommend-note">
          <span className="dc-pill warn">! thermally limited</span>{' '}
          Speed is capped so each layer can cool below {report.material.overpassTempC}°C
          (the {report.material.label} overpass temperature) before the nozzle returns —
          minimum {estimate.thermal.minLayerTimeS}s per layer.
        </div>
      )}
      {selected.coldBondRisk && (
        <div className="dc-recommend-note">
          <span className="dc-pill fail">✕ cold bonding risk</span>{' '}
          Layers take longer than {estimate.thermal.coldBondTimeS}s, so each layer cools below
          the {report.material.minBondTempC}°C bond temperature before the next bead lands —
          layer welds will be weak. Consider a smaller nozzle or printing several parts per bed.
        </div>
      )}
      {selected.slowestBand && (
        <div className="dc-recommend-note">
          Slowest layers sit around {selected.slowestBand.zMm}mm build height
          (~{selected.slowestBand.layerTimeS}s each,{' '}
          {selected.slowestBand.limitedBy === 'thermal' ? 'waiting for the layer below to cool' : 'limited by deposition speed'}).
        </div>
      )}
      <div className="dc-recommend-note">
        {printMode.overridden
          ? `You selected ${printMode.selected} mode (recommendation was ${printMode.recommended}). ${printMode.rationale}`
          : printMode.rationale}
      </div>
      {alternatives.length > 0 && (
        <div className="dc-recommend-note">
          Other modes: {alternatives.map((m) => `${m.mode} ~${formatPrintTime(m.printTimeH)} / ${m.weightKg}kg`).join(' · ')}
        </div>
      )}
      <div className="dc-recommend-note">
        Estimates are planning figures including a +{estimate.toolpathOverheadPct}% toolpath allowance,
        typically within ±20% of sliced toolpath time.
      </div>
    </div>
  );
}

function ChecksPanel({ report, orientation, guideMap }) {
  if (!report || !orientation) return null;
  const rec = report.recommended;
  const isRecommended = orientation.id === rec.orientationId;

  return (
    <div>
      <div className="dc-recommend-card">
        <h3>Recommended setup{isRecommended ? '' : ` (for ${rec.orientationId} orientation)`}</h3>
        <div className="dc-recommend-grid">
          <div className="dc-stat"><b>{rec.orientationId}</b><span>{rec.orientationLabel}</span></div>
          <div className="dc-stat"><b>{rec.nozzleMm}mm</b><span>Nozzle</span></div>
          <div className="dc-stat"><b>{rec.beadWidthMm}mm</b><span>Bead width</span></div>
          <div className="dc-stat"><b>{rec.layerHeightMm}mm</b><span>Layer height</span></div>
        </div>
        <div className="dc-recommend-note">{rec.wallStrategy}.</div>
      </div>

      <PrintEstimate report={report} orientation={orientation} />

      {report.part.bodyCount > 1 && (
        <div className="dc-check-row">
          <span className="dc-pill warn">! multi-body</span>
          <div className="dc-check-body">
            <div className="dc-check-title"><b>Multiple bodies in STEP file</b></div>
            <div className="dc-check-detail">
              The file contains {report.part.bodyCount} separate bodies, analysed as one merged part —
              the estimates blend all bodies. If these are separate parts, upload them individually.
            </div>
          </div>
        </div>
      )}

      {Object.entries(orientation.checks).map(([id, check]) => (
        <CheckRow key={id} id={id} check={check} guideMap={guideMap} />
      ))}
      <CheckRow id="wallThickness" check={report.wallThickness} guideMap={guideMap} />
      {Object.entries(report.partChecks || {}).map(([id, check]) => (
        <CheckRow key={id} id={id} check={check} guideMap={guideMap} />
      ))}

      {report.advancedStrategy && report.advancedStrategy.multiAxisCandidate && (
        <div className="dc-check-row">
          <span className="dc-pill warn">! multi-axis candidate</span>
          <div className="dc-check-body">
            <div className="dc-check-title"><b>Advanced deposition strategy may be needed</b></div>
            <div className="dc-check-detail">{report.advancedStrategy.detail}</div>
            <Link className="dc-check-link" to="/design-check/guide/006-advanced-strategies">
              Read why in the guide: Advanced Deposition Strategies →
            </Link>
          </div>
        </div>
      )}

      <div className="dc-check-row" style={{ background: 'transparent', border: 'none' }}>
        <div className="dc-check-detail">
          Part: {report.part.bboxMm.x} × {report.part.bboxMm.y} × {report.part.bboxMm.z}mm ·{' '}
          {report.part.volumeCm3.toLocaleString()}cm³ · {report.part.triangleCount.toLocaleString()} triangles ·
          cell {report.machine.envelopeMm.x} × {report.machine.envelopeMm.y} × {report.machine.envelopeMm.z}mm
        </div>
      </div>
    </div>
  );
}

export default ChecksPanel;
